import { useMemo } from "react"

import { useTranslation } from "react-i18next"

import Button from "../../../../../../../Components/Button"
import Select from "../../../../../../../Components/Select"
import Icon from "../../../../../../../Icon"

import styles from "./evidenceToolbar.module.less"

// Floating toolbar over the PDF (32px from the bottom). Left = document controls
// (zoom, page nav); right = the version picker. The dispute actions live in the topbar.
const EvidenceToolbar = ({
  pageCount,
  pageIndex,
  onGoToPage,
  pdfZoom,
  onZoomChange,
  version,
  onVersionChange,
  evidenceFiles
}) => {
  const { t } = useTranslation()

  // Version dropdown options, newest first. Memoized so the Select doesn't rebuild +
  // re-sort the array on every render.
  const versionOptions = useMemo(
    () =>
      evidenceFiles
        .map((e) => ({ value: e.version, label: `v${e.version}` }))
        .sort((a, b) => b.value - a.value),
    [evidenceFiles]
  )
  const multiVersion = evidenceFiles.length > 1

  return (
    <div className={styles.toolbar}>
      {/* Document controls (zoom + page nav) need decoded pages; the version picker
          stays reachable if the pages fail to load. */}
      {pageCount ? (
        <>
          <div className={styles.group}>
            <Button
              type="text"
              className={styles.icon}
              disabled={pdfZoom <= 0.5}
              onClick={() => onZoomChange(Math.max(0.5, +(pdfZoom - 0.25).toFixed(2)))}
              aria-label={t("Zoom out")}
            >
              <Icon type="minus" />
            </Button>
            <span className={styles.readout}>{Math.round(pdfZoom * 100)}%</span>
            <Button
              type="text"
              className={styles.icon}
              disabled={pdfZoom >= 1.25}
              onClick={() => onZoomChange(Math.min(1.25, +(pdfZoom + 0.25).toFixed(2)))}
              aria-label={t("Zoom in")}
            >
              <Icon type="plus" />
            </Button>
          </div>

          {pageCount > 1 ? (
            <>
              <span className={styles.divider} />
              <div className={styles.group}>
                <Button
                  type="text"
                  className={styles.icon}
                  disabled={pageIndex <= 0}
                  onClick={() => onGoToPage(pageIndex - 1)}
                  aria-label={t("Previous page")}
                >
                  <Icon type="chevron_down_small" className={styles.chevUp} />
                </Button>
                <span className={styles.readout}>
                  {pageIndex + 1} / {pageCount}
                </span>
                <Button
                  type="text"
                  className={styles.icon}
                  disabled={pageIndex >= pageCount - 1}
                  onClick={() => onGoToPage(pageIndex + 1)}
                  aria-label={t("Next page")}
                >
                  <Icon type="chevron_down_small" />
                </Button>
              </div>
            </>
          ) : null}
        </>
      ) : null}

      {multiVersion && pageCount ? <span className={styles.divider} /> : null}

      {multiVersion ? (
        <Select
          className={styles.versionSelect}
          value={version}
          onChange={onVersionChange}
          options={versionOptions}
          style={{ width: 80 }}
        />
      ) : version && pageCount ? (
        // A single version has no dropdown; still show which version is on screen.
        <span className={styles.readout}>v{version}</span>
      ) : null}
      {/* No Edit action: the evidence PDF is not editable by hand (RCV-5-36). */}
    </div>
  )
}

export default EvidenceToolbar
